import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import Pagination from "components/pagination";
import { apiCall } from "utils/apiClient";

const SERVICE_OFFERS_LIST_ENDPOINT = "admin/service-offers/list";

const parseDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const normalized = raw.includes("T")
    ? raw
    : raw.replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const parsed = parseDateTime(value);
  if (!parsed) return value || "-";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  const seconds = String(parsed.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const getRemainingTimeText = (endDateValue, nowMs) => {
  const endDate = parseDateTime(endDateValue);
  if (!endDate) return "";
  const diffMs = endDate.getTime() - nowMs;
  if (diffMs <= 0) return "Expired";

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m left`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
};

const ServiceOffersListPage = ({
  reloadKey,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [updatingStatusIds, setUpdatingStatusIds] = useState([]);
  const [deletingOfferIds, setDeletingOfferIds] = useState([]);
  const [nowMs, setNowMs] = useState(Date.now());

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const result = await apiCall({
        endpoint: SERVICE_OFFERS_LIST_ENDPOINT,
        method: "GET",
      });

      const offerData = Array.isArray(result?.data)
        ? result.data
        : [];

      // Normalize ID once
      const normalized = offerData.map((offer) => ({
        ...offer,
        id: String(offer?.id ?? offer?.offer_id ?? ""),
        is_active: Number(offer?.is_active) === 1 ? 1 : 0,
        discount_type:
          offer?.discount_type === "flat" ||
          offer?.discount_type === "fixed"
            ? "percentage"
            : offer?.discount_type,
      }));

      setOffers(normalized);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to fetch offers");
    } finally {
      setLoading(false);
    }
  };

  // Refetch when reloadKey changes
  useEffect(() => {
    fetchOffers();
  }, [reloadKey]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const totalRecords = offers.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalRecords / recordsPerPage)
  );
  const startIndex = (currentPage - 1) * recordsPerPage;
  const pageData = offers.slice(
    startIndex,
    startIndex + recordsPerPage
  );
  const displayedCount = Math.min(
    currentPage * recordsPerPage,
    totalRecords
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleDeleteClick = async (offer) => {
    const offerId = String(offer?.id ?? "");
    if (!offerId) {
      toast.error("Invalid offer id");
      return;
    }

    if (deletingOfferIds.includes(offerId)) return;

    setDeletingOfferIds((prev) => [...prev, offerId]);
    const deleted = await onDelete(offer);
    if (deleted) {
      setOffers((prev) =>
        prev.filter((item) => item.id !== offerId)
      );
    }
    setDeletingOfferIds((prev) =>
      prev.filter((id) => id !== offerId)
    );
  };

  const handleToggleStatus = async (offer) => {
    const offerId = String(offer?.id ?? "");

    if (!offerId) {
      toast.error("Invalid offer id");
      return;
    }

    const currentStatus = Number(offer?.is_active) === 1 ? 1 : 0;
    const nextStatus = currentStatus === 1 ? 0 : 1;

    setUpdatingStatusIds((prev) => [...prev, offerId]);
    setOffers((prev) =>
      prev.map((item) =>
        item.id === offerId ? { ...item, is_active: nextStatus } : item
      )
    );

    try {
      const result = await apiCall({
        endpoint: `admin/service-offers/status/${offerId}`,
        method: "POST",
        data: { is_active: nextStatus },
      });

      const isSuccess =
        Number(result?.status) === 200 ||
        Number(result?.status) === 201 ||
        Number(result?.status) === 204 ||
        result?.status === true ||
        Number(result?.code) === 200 ||
        Number(result?.code) === 201 ||
        result?.success === true;

      if (!isSuccess) {
        setOffers((prev) =>
          prev.map((item) =>
            item.id === offerId
              ? { ...item, is_active: currentStatus }
              : item
          )
        );
        toast.error(
          result?.message || "Failed to update offer status"
        );
        return;
      }

      toast.success(
        nextStatus === 1
          ? "Offer activated successfully"
          : "Offer deactivated successfully"
      );
      fetchOffers();
    } catch (error) {
      setOffers((prev) =>
        prev.map((item) =>
          item.id === offerId
            ? { ...item, is_active: currentStatus }
            : item
        )
      );
      console.error("Status update error:", error);
      toast.error("Failed to update offer status");
    } finally {
      setUpdatingStatusIds((prev) =>
        prev.filter((id) => id !== offerId)
      );
    }
  };

  return (
    <div className="border rounded-lg bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-700">
          Offers List
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="px-3 py-1 rounded bg-indigo-600 text-white text-sm"
        >
          Add
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-gray-500 text-center">
          Loading offers...
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-2">Sr No</th>
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Value</th>
                  <th className="text-left p-2">Start</th>
                  <th className="text-left p-2">End</th>
                  <th className="text-left p-2">Priority</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageData.length > 0 ? (
                  pageData.map((offer, index) => (
                    <tr key={offer.id} className="border-b">
                      <td className="p-2">
                        {startIndex + index + 1}
                      </td>
                      <td className="p-2">{offer.title}</td>
                      <td className="p-2">
                        {offer.discount_type}
                      </td>
                      <td className="p-2">
                        {offer.discount_value}
                      </td>
                      <td className="p-2">
                        {formatDateTime(offer.start_date)}
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col">
                          <span>
                            {formatDateTime(offer.end_date)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {getRemainingTimeText(
                              offer.end_date,
                              nowMs
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="p-2">
                        {offer.priority}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <Toggle
                            checked={Number(offer.is_active) === 1}
                            onChange={() => handleToggleStatus(offer)}
                            disabled={updatingStatusIds.includes(
                              offer.id
                            )}
                            icons={false}
                            className="scale-75"
                          />
                          <span>
                            {Number(offer.is_active) === 1
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </div>
                      </td>
                      <td className="p-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(offer)}
                          className="px-2 py-1 rounded bg-blue-600 text-white text-xs"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteClick(offer)
                          }
                          disabled={deletingOfferIds.includes(
                            offer.id
                          )}
                          className="px-2 py-1 rounded bg-red-600 text-white text-xs"
                        >
                          {deletingOfferIds.includes(offer.id)
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      className="p-3 text-center text-gray-500"
                    >
                      No offers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={(value) => {
              setCurrentPage(1);
              setRecordsPerPage(value);
            }}
            totalRecords={totalRecords}
            count={displayedCount}
          showFirstLastControls
          compact
          />
        </>
      )}
    </div>
  );
};

ServiceOffersListPage.propTypes = {
  reloadKey: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ServiceOffersListPage;
